import { useState, useRef, useEffect, useCallback } from 'react';
import { categories, siteInfo } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-t-shirt-line',
  'T-Shirts': 'ri-shirt-line',
  'Jackets': 'ri-t-shirt-line',
  'Pants/Shorts': 'ri-t-shirt-line',
  'Headwear': 'ri-sun-line',
  'Sets': 'ri-layout-grid-line',
  'Underwear/Underpants': 'ri-shirt-line',
  'Jersey': 'ri-basketball-line',
  'Accessories': 'ri-glasses-line',
  'Bags': 'ri-handbag-line',
  'Belts': 'ri-link',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

const clothingCategories = categories.filter(c =>
  ['Shoes', 'Hoodies/Sweaters', 'T-Shirts', 'Jackets', 'Pants/Shorts', 'Sets', 'Jersey', 'Underwear/Underpants'].includes(c.name)
);
const accessoryCategories = categories.filter(c =>
  ['Headwear', 'Accessories', 'Bags', 'Belts', 'Perfumes'].includes(c.name)
);
const otherCategories = categories.filter(c =>
  ['Other Stuff'].includes(c.name)
);

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a
            href="/"
            className={`flex items-center gap-2 whitespace-nowrap ${
              isScrolled ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            <span className="text-lg md:text-xl font-heading font-semibold tracking-tight">
              {siteInfo.name}
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/80 overflow-hidden py-1 animate-[fadeIn_0.15s_ease-out]">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-sm`}></i>
                      </span>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-400">({cat.count})</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative group">
              <button
                className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                Platform Guides
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/80 overflow-hidden py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <a href="/hipobuy-spreadsheet-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">Hipobuy Spreadsheet</a>
                <a href="/kakobuy-spreadsheet" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">Kakobuy Spreadsheet</a>
                <a href="/cssbuy-spreadsheet" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">CSSBuy Spreadsheet</a>
                <a href="/superbuy-spreadsheet" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">Superbuy Spreadsheet</a>
                <a href="/oopbuy-spreadsheet" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">Oopbuy Spreadsheet</a>
              </div>
            </div>

            <a
              href={siteInfo.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              hipobuy sheet
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="/hipobuy-spreadsheet-guide"
              className="px-5 py-2 text-sm font-medium text-foreground-700 bg-background-50/90 hover:bg-background-50 rounded-full transition-all cursor-pointer whitespace-nowrap border border-background-200/50"
            >
              Read Guide
            </a>
            <a
              href={siteInfo.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              buy it now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-foreground-900' : 'text-background-50'
            }`}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`text-xl transition-transform duration-200 ${isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto animate-[slideDown_0.25s_ease-out]">
          <div className="px-4 py-4 space-y-1">
            <a
              href={siteInfo.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-background-50 bg-primary-500 rounded-xl cursor-pointer whitespace-nowrap mb-4"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-base"></i>
              </span>
              buy it now
              <i className="ri-arrow-right-line ml-auto"></i>
            </a>

            <button
              onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
              className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-900 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                  <i className="ri-grid-line"></i>
                </span>
                Product Categories
              </span>
              <i className={`ri-arrow-down-s-line text-sm text-foreground-400 transition-transform duration-200 ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCategoryOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/60 space-y-0.5 animate-[fadeIn_0.2s_ease-out]">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-widest px-3 py-2">Clothing</p>
                {clothingCategories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-sm`}></i>
                    </span>
                    {cat.name}
                    <span className="ml-auto text-xs text-foreground-400">{cat.count}</span>
                  </a>
                ))}

                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-widest px-3 py-2 pt-2">Accessories</p>
                {accessoryCategories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-sm`}></i>
                    </span>
                    {cat.name}
                    <span className="ml-auto text-xs text-foreground-400">{cat.count}</span>
                  </a>
                ))}

                {otherCategories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-sm`}></i>
                    </span>
                    {cat.name}
                    <span className="ml-auto text-xs text-foreground-400">{cat.count}</span>
                  </a>
                ))}
              </div>
            )}

            <button
              onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
              className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-900 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                  <i className="ri-book-open-line"></i>
                </span>
                Platform Guides
              </span>
              <i className={`ri-arrow-down-s-line text-sm text-foreground-400 transition-transform duration-200 ${mobileGuidesOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileGuidesOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/60 space-y-0.5 animate-[fadeIn_0.2s_ease-out]">
                <a href="/hipobuy-spreadsheet-guide" className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <span className="text-xs font-bold">H</span>
                  </span>
                  Hipobuy Spreadsheet
                </a>
                <a href="/kakobuy-spreadsheet" className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                  <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                    <span className="text-xs font-bold">K</span>
                  </span>
                  Kakobuy Spreadsheet
                </a>
                <a href="/cssbuy-spreadsheet" className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                  <span className="w-5 h-5 flex items-center justify-center text-secondary-500">
                    <span className="text-xs font-bold">C</span>
                  </span>
                  CSSBuy Spreadsheet
                </a>
                <a href="/superbuy-spreadsheet" className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                    <span className="text-xs font-bold">S</span>
                  </span>
                  Superbuy Spreadsheet
                </a>
                <a href="/oopbuy-spreadsheet" className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                    <span className="text-xs font-bold">O</span>
                  </span>
                  Oopbuy Spreadsheet
                </a>
              </div>
            )}

            <div className="border-t border-background-200/60 my-2"></div>

            <a
              href={siteInfo.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-external-link-line"></i>
              </span>
              hipobuy sheet — Main Store
            </a>

            <a
              href="/hipobuy-spreadsheet-guide"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-article-line"></i>
              </span>
              Read Our Complete Guide
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}